import { I18n } from '@affine/i18n';
import { AIStarIconWithAnimation } from '@blocksuite/affine/components/icons';
import {
  type MermaidActionInput,
  MermaidActionProvider,
  type MermaidActionService,
  type MermaidExternalAction,
} from '@blocksuite/affine/shared/services';
import { LifeCycleWatcher } from '@blocksuite/affine/std';
import type { Container } from '@blocksuite/global/di';

import { actionToHandler } from '../actions/edgeless-handler';
import type { AIContext } from '../utils/context';
import {
  buildMermaidAIInput,
  type MermaidAIIntent,
  type MermaidAIRequest,
  validateNormalizedMermaid,
} from '../utils/mermaid-ai';

export class MermaidAIActionService
  extends LifeCycleWatcher
  implements MermaidActionService
{
  static override readonly key = 'affine-mermaid-ai-action-service';

  static override setup(di: Container) {
    super.setup(di);
    di.addImpl(MermaidActionProvider, provider => provider.get(this));
  }

  can(action: MermaidExternalAction, input: MermaidActionInput) {
    return (
      (action === 'modify-ai' || action === 'repair-ai') &&
      input.source.trim().length > 0
    );
  }

  run(action: MermaidExternalAction, input: MermaidActionInput) {
    if (!this.can(action, input)) return;

    const intent: MermaidAIIntent =
      action === 'modify-ai' ? 'modify' : 'repair';
    const forceInput = intent === 'modify';

    const handler = actionToHandler(
      'generateMermaid',
      AIStarIconWithAnimation,
      { intent },
      async (_host, ctx: AIContext, panelInput?: string) => {
        const instruction = panelInput?.trim() ?? '';
        if (intent === 'modify' && !instruction) return;

        let parserError = '';
        if (intent === 'repair') {
          const validation = await validateNormalizedMermaid(input.source);
          if (!validation.valid) {
            parserError = validation.error;
          }
        }

        const request: MermaidAIRequest = {
          intent,
          locale: I18n.language,
          currentSource: input.source,
          instruction:
            intent === 'repair'
              ? 'Repair the Mermaid syntax without changing the intended meaning.'
              : instruction,
          parserError,
        };

        ctx.set({
          mermaidRequest: request,
          mermaidTargetElementId: input.elementId,
          mermaidTargetSource: input.source,
        });

        return {
          input: buildMermaidAIInput(request),
        };
      },
      undefined,
      forceInput
    );

    handler(this.std.host);
  }
}
